import React from "react";
import {
	CardinalLocation,
	ClassNameProps,
	compareBooleansDesc,
	compareObjects,
	If,
	List,
	makeClassName,
	NullablePick,
	Row,
	sort,
	StyleProps,
	toGrammaticalNumber,
	Tooltip,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { Person, PersonNameFragment, truncateDisplayItems } from "../../../../Utilities";
import { PersonFormat } from "../../../Format";
import styles from "./styles.module.css";

const tooltipSideOrder: CardinalLocation[] = ["bottom", "top", "right", "left"];

export type TaggedPeopleViewProps = {
	taggedPeople: (Pick<Person, "id"> & PersonNameFragment & { user: NullablePick<Person["user"], "isCurrent"> })[];
} & Partial<ClassNameProps & StyleProps>;

export function TaggedPeopleView({ taggedPeople, className, style }: TaggedPeopleViewProps) {
	const sortedTaggedPeople = React.useMemo(
		() =>
			sort(
				taggedPeople,
				compareObjects((e) => e.user?.isCurrent ?? false, compareBooleansDesc()),
			),
		[taggedPeople],
	);

	if (taggedPeople.length === 0) {
		return null;
	}

	const firstTaggedPerson = sortedTaggedPeople[0];
	const otherTaggedPeople = sortedTaggedPeople.slice(1);
	const {
		displayedItems: displayedOtherTaggedPeople,
		anyHidden: anyOtherTaggedPeopleHidden,
		numHidden: numOtherTaggedPeopleHidden,
	} = truncateDisplayItems(otherTaggedPeople, 8);

	return (
		<Row
			justify="spaced-start"
			align="center"
			horizontalSpacing="0.5rem"
			className={makeClassName(styles["tagged-people"], className)}
			style={style}
		>
			<Icon.Camera size={16} />
			<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
				<span className={styles["person-name"]}>{PersonFormat.name(firstTaggedPerson)}</span>
				<If condition={otherTaggedPeople.length > 0}>
					and
					<Tooltip.Container sideOrder={tooltipSideOrder}>
						<Tooltip className={styles["tagged-people__others-tooltip"]}>
							<Tooltip.Body>
								<List className={styles["tagged-people__others-list"]}>
									{displayedOtherTaggedPeople.map((person) => (
										<List.Item key={person.id} className="nowrap">
											{PersonFormat.name(person)}
										</List.Item>
									))}
									<If condition={anyOtherTaggedPeopleHidden}>
										<span className="nowrap">
											And {toGrammaticalNumber("Other", numOtherTaggedPeopleHidden, true)}...
										</span>
									</If>
								</List>
							</Tooltip.Body>
						</Tooltip>

						<span className={styles["person-name"]}>
							{toGrammaticalNumber("other", sortedTaggedPeople.length - 1, true)}
						</span>
					</Tooltip.Container>
				</If>
			</Row>
		</Row>
	);
}
